/**
 * The hub's side panel lives in `?panel=` (see `PANELS` in `config.js`):
 * `new`, `table:12`, `blocks:5`, `conditions:5`. Links build their
 * location here, so the panel opens over the tab already open, or over
 * the panel's own tab when the link comes from outside the hub.
 */
import { PANELS } from "./config";

const PANEL_PATTERN = /^([a-z]+)(?::(\d+))?$/;

/** `"blocks:5"` → `{ kind: "blocks", id: 5, def }`; null when unknown or malformed. */
export function parsePanel(value) {
  const match = typeof value === "string" ? PANEL_PATTERN.exec(value) : null;
  const def = match ? PANELS[match[1]] : null;
  if (!def || !!def.withId !== !!match[2]) {
    return null;
  }
  return { kind: match[1], id: match[2] ? Number(match[2]) : null, def };
}

/** The query without the panel and the parameters only its page reads. */
export function withoutPanel(query) {
  const rest = { ...query };
  const parsed = parsePanel(rest.panel);
  delete rest.panel;
  ((parsed && parsed.def.extras) || []).forEach((key) => delete rest[key]);
  return rest;
}

/**
 * Where a link to a panel goes: `kind` and `id` name it, `extra` are its own parameters (`client_no`…). Inside
 * the hub the open tab stays (the panel opens over it, a panel already open gives way); outside it the panel's
 * home tab is laid underneath.
 */
export function panelLocation(route, kind, id = null, extra = {}) {
  const def = PANELS[kind];
  const base = route.path === "/quotes" ? withoutPanel(route.query) : { ...(def.home || {}) };
  const query = { ...base, panel: id !== null && id !== undefined ? `${kind}:${id}` : kind };
  Object.keys(extra).forEach((key) => {
    if (extra[key] !== null && extra[key] !== undefined && extra[key] !== "") {
      query[key] = String(extra[key]);
    }
  });
  return { path: "/quotes", query };
}

/**
 * Opens a panel from anywhere: a new entry in the history (back closes it), or, from one panel to another
 * (blocks ↔ conditions), in place of the open one, so one back still closes it.
 */
export function openPanel(vm, kind, id = null, extra = {}) {
  const location = panelLocation(vm.$route, kind, id, extra);
  const replace = vm.$route.path === "/quotes" && !!vm.$route.query.panel;
  return (replace ? vm.$router.replace(location) : vm.$router.push(location)).catch(() => {});
}
